import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { FileText, ExternalLink } from 'lucide-react';
const exampleData = [{
  id: '1001',
  date: '2023-01-15',
  type: 'Contract',
  summary: 'Master Service Agreement between Acme Corp and Widget Inc. Establishes 3-year initial term with auto-renewal provisions. Total contract value $150,000 annually with pricing escalation clause.'
}, {
  id: '1002',
  date: '2023-02-03',
  type: 'Email',
  summary: 'Internal email from CFO to CEO discussing Widget Inc. contract concerns. Mentions potential breach of exclusivity terms. Requests legal review before next board meeting.'
}, {
  id: '1003',
  date: '2023-02-10',
  type: 'Invoice',
  summary: 'Q1 2023 invoice from Widget Inc. to Acme Corp for services rendered. Shows $37,500 charged against MSA. Payment terms net 30 days.'
}];
export function Deliverable() {
  return <section id="deliverables" className="py-24 bg-slate-50" data-id="element-31">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-33">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-34">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-6" data-id="element-35">
              Standard Deliverable: <br data-id="element-36" />
              <span className="text-amber-600" data-id="element-37">Clean, Actionable Data</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6" data-id="element-38">
              The output is delivered in a simple Excel spreadsheet — one row
              per document — that your team can sort, filter, and search
              immediately. No special software required, no learning curve.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-39">
              {['Date - contract date, email date, creation date', 'Document Type - contract, email, memo, invoice, etc.', 'Summary - 2-3 sentences explaining content', 'Link - direct access to view original document'].map((item, i) => <li key={i} className="flex items-start" data-id="element-40">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-41">
                    <div className="h-2 w-2 rounded-full bg-amber-600" data-id="element-42" />
                  </div>
                  <span className="ml-3 text-slate-700" data-id="element-43">{item}</span>
                </li>)}
            </ul>
            <p className="text-sm text-slate-500 italic border-l-4 border-slate-200 pl-4" data-id="element-44">
              "If you need deeper analysis later (e.g., 'find everything related
              to the Widget Inc. exclusivity clause'), we run a second targeted
              pass with a custom prompt."
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-45">
            <div className="bg-slate-900 px-4 py-3 border-b border-slate-800 flex items-center" data-id="element-46">
              <div className="flex space-x-2 mr-4" data-id="element-47">
                <div className="w-3 h-3 rounded-full bg-red-500/20" data-id="element-48" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/20" data-id="element-49" />
                <div className="w-3 h-3 rounded-full bg-green-500/20" data-id="element-50" />
              </div>
              <span className="text-slate-400 text-xs font-mono" data-id="element-51">
                Evantiq_Analysis_Export.xlsx
              </span>
            </div>

            <div className="overflow-x-auto" data-id="element-52">
              <table className="min-w-full divide-y divide-slate-200 text-sm" data-id="element-53">
                <thead className="bg-slate-50" data-id="element-54">
                  <tr data-id="element-55">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-56">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-57">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-58">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-59">
                      Summary
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-60">
                      Link
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200" data-id="element-61">
                  {exampleData.map((row, idx) => <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} data-id="element-62">
                      <td className="px-6 py-4 whitespace-nowrap text-slate-500 font-mono text-xs" data-id="element-63">
                        {row.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate-900 font-medium" data-id="element-64">
                        {row.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-65">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${row.type === 'Contract' ? 'bg-blue-100 text-blue-800' : row.type === 'Email' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`} data-id="element-66">
                          {row.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600 min-w-[300px]" data-id="element-67">
                        {row.summary}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-amber-600 hover:text-amber-700 cursor-pointer" data-id="element-68">
                        <div className="flex items-center" data-id="element-69">
                          <span className="mr-1" data-id="element-70">View</span>
                          <ExternalLink size={12} data-id="element-71" />
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 text-xs text-slate-500 flex justify-between" data-id="element-72">
              <span data-id="element-73">Showing 3 of 1,248 documents</span>
              <span data-id="element-74">Processed in 4h 12m</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}